import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CalendarDayModal } from '../../components/CalendarDayModal';
import FloatingAlert from '../../components/FloatingAlert';
import Loader from '../../components/Loader';
import { ModalBase } from '../../components/ModalBase';
import { useAuth } from '../../context/AuthContext';
import { PeriodDto, UpdatePeriodRequestDto } from '../../interfaces/period';
import { emotionService } from '../../services/EmotionService';
import { flowService } from '../../services/FlowService';
import { periodService } from '../../services/PeriodService';
import { symptomService } from '../../services/SymptomService';
import './../../styles/calendar/calendar-design.css';
import './../../styles/calendar/calendar-page.css';

const DAY_IN_MS = 24 * 60 * 60 * 1000;

type PendingPeriodEdit = {
    title: string;
    description: string;
    confirmLabel: string;
    isDanger?: boolean;
    action:
    | { type: 'delete'; periodId: number }
    | { type: 'update'; payload: UpdatePeriodRequestDto };
};

const CustomCalendar = () => {
    // Helper function to capitalize the first letter of a string
    const capitalizeFirst = (str: string) => {
        return str.charAt(0).toUpperCase() + str.slice(1);
    };

    const formatDateLabel = (date: Date) =>
        capitalizeFirst(
            date.toLocaleDateString('es-ES', {
                day: '2-digit',
                month: 'long',
                year: 'numeric',
            })
        );
    const [openModal, setOpenModal] = useState<boolean>(false);
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);

    // --- Estados para lógica y CRUD del modal ---
    const [isLoadingModal, setIsLoadingModal] = useState(false);
    const [modalError, setModalError] = useState<string | null>(null);
    const [isEditingPeriod, setIsEditingPeriod] = useState(false);
    const [showDeleteWarning, setShowDeleteWarning] = useState(false);
    const [pendingPeriodEdit, setPendingPeriodEdit] = useState<PendingPeriodEdit | null>(null);
    const [isSavingPeriod, setIsSavingPeriod] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isSavingFlow, setIsSavingFlow] = useState(false);
    const [isDeletingFlow, setIsDeletingFlow] = useState(false);
    const [isSavingSymptoms, setIsSavingSymptoms] = useState(false);
    const [isSavingEmotions, setIsSavingEmotions] = useState(false);
    const [flowSaveStatus, setFlowSaveStatus] = useState<'idle' | 'success' | 'error'>('idle');
    const [symptomsSaveStatus, setSymptomsSaveStatus] = useState<'idle' | 'success' | 'error'>('idle');
    const [emotionsSaveStatus, setEmotionsSaveStatus] = useState<'idle' | 'success' | 'error'>('idle');
    const previousFlowRef = useRef<{ existing: import('../../interfaces/flow').FlowDto | null } | null>(null);
    const previousSymptomsRef = useRef<number[] | null>(null);
    const previousEmotionsRef = useRef<number[] | null>(null);

    // Flujos
    const [flowLevel, setFlowLevel] = useState<import('../../interfaces/flow').FlowDto | null>(null);
    const [existingFlow, setExistingFlow] = useState<import('../../interfaces/flow').FlowDto | null>(null);
    // Síntomas
    const [selectedSymptoms, setSelectedSymptoms] = useState<number[]>([]);
    const [existingSymptoms, setExistingSymptoms] = useState<number[]>([]);
    // Emociones
    const [selectedEmotions, setSelectedEmotions] = useState<number[]>([]);
    const [existingEmotions, setExistingEmotions] = useState<number[]>([]);

    const [currentDate, setCurrentDate] = useState(new Date());
    const [periods, setPeriods] = useState<PeriodDto[]>([]);
    const [isLoadingPeriods, setIsLoadingPeriods] = useState<boolean>(true);
    const [flujosCatalogo, setFlujosCatalogo] = useState<any[]>([]);
    const [sintomasCatalogo, setSintomasCatalogo] = useState<any[]>([]);
    const [emocionesCatalogo, setEmocionesCatalogo] = useState<any[]>([]);
    const [allFlows, setAllFlows] = useState<import('../../interfaces/flow').FlowDto[]>([]);
    const [allSymptoms, setAllSymptoms] = useState<any[]>([]);
    const [allEmotions, setAllEmotions] = useState<any[]>([]);
    const { user } = useAuth();

    // ALERTAS
    const [showAlerts, setShowAlerts] = useState({
        success: true,
        warning: true,
        danger: true,
        info: true,
    });
    const [futureSelectionAlert, setFutureSelectionAlert] = useState<{ id: number; message: string } | null>(null);


    const PinIcon = () => (
        <span
            className='calendar-pin-icon'
            style={{
                fontSize: '1.15em',
                color: '#1a237e',
                verticalAlign: 'middle',
                filter: 'drop-shadow(0 0 1px #fff)',
            }}
            title='Hay datos registrados'
        >📌</span>
    );

    // --- Mapas rápidos por fecha para la grilla ---
    const flowByDate = useMemo(() => {
        const map: Record<string, boolean> = {};
        allFlows.forEach((flow) => {
            if (flow.fecha) map[flow.fecha] = true;
        });
        return map;
    }, [allFlows]);

    const symptomsByDate = useMemo(() => {
        const map: Record<string, boolean> = {};
        allSymptoms.forEach((s: any) => {
            if (s.fecha) map[s.fecha] = true;
        });
        return map;
    }, [allSymptoms]);

    const emotionsByDate = useMemo(() => {
        const map: Record<string, boolean> = {};
        allEmotions.forEach((e: any) => {
            if (e.fecha) map[e.fecha] = true;
        });
        return map;
    }, [allEmotions]);


    // --- Utilidades y lógica de negocio para el modal ---
    // Buscar el periodo relevante para la fecha seleccionada
    const findPeriodForDate = (date: Date) => {
        for (const period of periods) {
            const periodStartStr = period.fecha_inicio.split('T')[0];
            const periodStart = new Date(periodStartStr + 'T00:00:00');
            periodStart.setHours(0, 0, 0, 0);
            let periodEnd: Date;
            if (period.fecha_termino) {
                const periodEndStr = period.fecha_termino.split('T')[0];
                periodEnd = new Date(periodEndStr + 'T00:00:00');
                periodEnd.setHours(0, 0, 0, 0);
                if (date >= periodStart && date <= periodEnd) {
                    return period;
                }
            } else {
                if (date.getTime() === periodStart.getTime()) {
                    return period;
                }
            }
        }
        return null;
    };

    // Obtener el periodo anterior más cercano (para días fuera de periodo)
    const getClosestPreviousPeriod = (date: Date) => {
        const selected = new Date(date);
        selected.setHours(0, 0, 0, 0);
        let closest = null;
        let minDiff = Infinity;
        for (const period of periods) {
            const end = new Date((period.fecha_termino || period.fecha_inicio).split('T')[0] + 'T00:00:00');
            end.setHours(0, 0, 0, 0);
            const diff = (selected.getTime() - end.getTime()) / (1000 * 60 * 60 * 24);
            if (diff >= 0 && diff < minDiff) {
                minDiff = diff;
                closest = period;
            }
        }
        return closest;
    };

    // --- Lógica para cargar datos al abrir el modal ---
    const loadModalData = async (date: Date) => {
        setIsLoadingModal(true);
        setModalError(null);
        setIsEditingPeriod(false);
        setPendingPeriodEdit(null);
        setFlowLevel(null);
        setExistingFlow(null);
        setSelectedSymptoms([]);
        setExistingSymptoms([]);
        setSelectedEmotions([]);
        setExistingEmotions([]);
        const period = findPeriodForDate(date);
        const closestPeriod = period || getClosestPreviousPeriod(date);
        const dayStr = date.toISOString().split('T')[0];
        try {
            if (closestPeriod) {
                const [flowRes, symptomRes, emotionRes] = await Promise.all([
                    period ? flowService.getFlowByIdPeriodo(closestPeriod.id_periodo) : Promise.resolve({ statusCode: 200, flujos: [] }),
                    symptomService.getSymptomsByIdPeriodo(closestPeriod.id_periodo),
                    emotionService.getEmotionsByIdPeriodo(closestPeriod.id_periodo),
                ]);
                // Flujos
                if (period && flowRes.statusCode === 200 && Array.isArray(flowRes.flujos)) {
                    const flowForDay = flowRes.flujos.find(f => f.fecha === dayStr);
                    if (flowForDay) {
                        setExistingFlow(flowForDay);
                        const found = flujosCatalogo.find(f => f.id_flujo === flowForDay.id_flujo);
                        setFlowLevel(found || null);
                    } else {
                        setExistingFlow(null);
                        setFlowLevel(null);
                    }
                } else {
                    setExistingFlow(null);
                    setFlowLevel(null);
                }
                // Síntomas (verificar si existe campo fecha antes de filtrar)
                if (symptomRes.statusCode === 200 && Array.isArray(symptomRes.sintomas)) {
                    let symptomsForDay = symptomRes.sintomas;
                    if (symptomRes.sintomas.length && 'fecha' in symptomRes.sintomas[0]) {
                        symptomsForDay = symptomRes.sintomas.filter((s: any) => s.fecha === dayStr);
                    }
                    const ids = symptomsForDay.map((s: any) => Number(s.id_sintoma));
                    setExistingSymptoms(ids);
                    setSelectedSymptoms(ids);
                } else {
                    setExistingSymptoms([]);
                    setSelectedSymptoms([]);
                }
                // Emociones (verificar si existe campo fecha antes de filtrar)
                if (emotionRes.statusCode === 200 && Array.isArray(emotionRes.emociones)) {
                    let emotionsForDay = emotionRes.emociones;
                    if (emotionRes.emociones.length && 'fecha' in emotionRes.emociones[0]) {
                        emotionsForDay = emotionRes.emociones.filter((e: any) => e.fecha === dayStr);
                    }
                    const ids = emotionsForDay.map((e: any) => Number(e.id_emocion));
                    setExistingEmotions(ids);
                    setSelectedEmotions(ids);
                } else {
                    setExistingEmotions([]);
                    setSelectedEmotions([]);
                }
            } else {
                setExistingFlow(null);
                setFlowLevel(null);
                setExistingSymptoms([]);
                setSelectedSymptoms([]);
                setExistingEmotions([]);
                setSelectedEmotions([]);
            }
        } catch (e) {
            const err = e as any;
            setModalError(err.message || 'Error al cargar los datos del día.');
        } finally {
            setIsLoadingModal(false);
        }
    };

    // Extraer función para cargar todos los flujos de todos los periodos
    const fetchAllFlows = async (periodsList = periods) => {
        if (!periodsList.length) {
            setAllFlows([]);
            return;
        }
        let flows: import('../../interfaces/flow').FlowDto[] = [];
        for (const period of periodsList) {
            if (!period.id_periodo) continue;
            try {
                const res = await flowService.getFlowByIdPeriodo(period.id_periodo);
                if (res.statusCode === 200 && Array.isArray(res.flujos)) {
                    flows = flows.concat(res.flujos);
                }
            } catch { }
        }
        setAllFlows(flows);
    };

    // Cargar todos los síntomas y emociones de todos los periodos
    const fetchAllSymptoms = async (periodsList = periods) => {
        if (!periodsList.length) {
            setAllSymptoms([]);
            return;
        }
        let symptoms: any[] = [];
        for (const period of periodsList) {
            if (!period.id_periodo) continue;
            try {
                const res = await symptomService.getSymptomsByIdPeriodo(period.id_periodo);
                if (res.statusCode === 200 && Array.isArray(res.sintomas)) {
                    symptoms = symptoms.concat(res.sintomas);
                }
            } catch { }
        }
        setAllSymptoms(symptoms);
    };

    const fetchAllEmotions = async (periodsList = periods) => {
        if (!periodsList.length) {
            setAllEmotions([]);
            return;
        }
        let emotions: any[] = [];
        for (const period of periodsList) {
            if (!period.id_periodo) continue;
            try {
                const res = await emotionService.getEmotionsByIdPeriodo(period.id_periodo);
                if (res.statusCode === 200 && Array.isArray(res.emociones)) {
                    emotions = emotions.concat(res.emociones);
                }
            } catch { }
        }
        setAllEmotions(emotions);
    };

    // Al cargar periodos, también carga todos los flujos de todos los periodos
    useEffect(() => {
        fetchAllFlows();
        fetchAllSymptoms();
        fetchAllEmotions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [periods]);

    // Guardar o actualizar flujo (recarga solo el flujo del día desde la API)
    const handleSaveFlow = async () => {
        if (!selectedDate) return;
        const period = findPeriodForDate(selectedDate);
        if (!period || !flowLevel) return;
        previousFlowRef.current = { existing: existingFlow };
        setFlowSaveStatus('idle');
        setIsSavingFlow(true);
        setModalError(null);
        try {
            const fecha = selectedDate.toISOString().split('T')[0];
            if (existingFlow) {
                await flowService.updateFlow({
                    id_periodo: period.id_periodo,
                    id_flujo: flowLevel.id_flujo,
                    fecha,
                });
            } else {
                await flowService.createFlowOfPeriod({
                    id_periodo: period.id_periodo,
                    id_flujo: flowLevel.id_flujo,
                    fecha,
                });
            }
            // Recargar solo el flujo del día y todos los flujos
            const flowRes = await flowService.getFlowByIdPeriodo(period.id_periodo);
            if (flowRes.statusCode === 200 && Array.isArray(flowRes.flujos)) {
                const flowForDay = flowRes.flujos.find((f: any) => f.fecha === fecha);
                setExistingFlow(flowForDay || null);
                if (flowForDay) {
                    const found = flujosCatalogo.find(f => f.id_flujo === flowForDay.id_flujo);
                    setFlowLevel(found || null);
                } else {
                    setFlowLevel(null);
                }
            } else {
                setExistingFlow(null);
                setFlowLevel(null);
            }
            await fetchAllFlows();
            setFlowSaveStatus('success');
        } catch (e: any) {
            setFlowSaveStatus('error');
            const previous = previousFlowRef.current?.existing || null;
            setExistingFlow(previous);
            if (previous) {
                const fallback = flujosCatalogo.find(f => f.id_flujo === previous.id_flujo) || null;
                setFlowLevel(fallback);
            } else {
                setFlowLevel(null);
            }
            setModalError(e.message || 'Error al guardar el flujo.');
        } finally {
            setIsSavingFlow(false);
        }
    };

    // Eliminar flujo (recarga solo el flujo del día desde la API)
    const handleDeleteFlow = async () => {
        if (!selectedDate) return;
        const period = findPeriodForDate(selectedDate);
        if (!period || !existingFlow) return;
        setIsDeletingFlow(true);
        setModalError(null);
        try {
            const fecha = selectedDate.toISOString().split('T')[0];
            await flowService.deleteFlow({
                id_periodo: period.id_periodo,
                fecha,
            });
            // Recargar solo el flujo del día y todos los flujos
            const flowRes = await flowService.getFlowByIdPeriodo(period.id_periodo);
            if (flowRes.statusCode === 200 && Array.isArray(flowRes.flujos)) {
                const flowForDay = flowRes.flujos.find((f: any) => f.fecha === fecha);
                setExistingFlow(flowForDay || null);
                if (flowForDay) {
                    const found = flujosCatalogo.find(f => f.id_flujo === flowForDay.id_flujo);
                    setFlowLevel(found || null);
                } else {
                    setFlowLevel(null);
                }
            } else {
                setExistingFlow(null);
                setFlowLevel(null);
            }
            await fetchAllFlows();
        } catch (e: any) {
            setModalError(e.message || 'Error al eliminar el flujo.');
        } finally {
            setIsDeletingFlow(false);
        }
    };

    // Guardar o actualizar síntomas (solo actualiza el estado relevante)
    const handleSaveSymptoms = async () => {
        if (!selectedDate) return;
        const period = findPeriodForDate(selectedDate) || getClosestPreviousPeriod(selectedDate);
        if (!period) {
            setModalError('No hay un periodo anterior para asociar los síntomas.');
            return;
        }
        previousSymptomsRef.current = [...existingSymptoms];
        setSymptomsSaveStatus('idle');
        setIsSavingSymptoms(true);
        setModalError(null);
        const fecha = selectedDate.toISOString().split('T')[0];
        try {
            const toCreate = selectedSymptoms.filter(id => !existingSymptoms.includes(id));
            const toDelete = existingSymptoms.filter(id => !selectedSymptoms.includes(id));
            if (toCreate.length > 0) {
                const createPayload = toCreate.map(id => ({
                    id_periodo: period.id_periodo,
                    id_sintoma: id,
                    fecha,
                    intensidad: 1,
                    notas: '',
                }));
                await symptomService.createSymptom(createPayload);
            }
            if (toDelete.length > 0) {
                const deletePayload = toDelete.map(id => ({
                    id_periodo: period.id_periodo,
                    id_sintoma: id,
                    fecha,
                }));
                await symptomService.deleteSymptom(deletePayload);
            }
            const nextSymptoms = [...selectedSymptoms];
            setExistingSymptoms(nextSymptoms);
            await fetchAllSymptoms();
            setSymptomsSaveStatus('success');
        } catch (e: any) {
            setSymptomsSaveStatus('error');
            const previous = previousSymptomsRef.current ? [...previousSymptomsRef.current] : [];
            setExistingSymptoms(previous);
            setSelectedSymptoms(previous);
            setModalError(e.message || 'Error al guardar los síntomas.');
        } finally {
            setIsSavingSymptoms(false);
        }
    };

    // Guardar o actualizar emociones (solo actualiza el estado relevante)
    const handleSaveEmotions = async () => {
        if (!selectedDate) return;
        const period = findPeriodForDate(selectedDate) || getClosestPreviousPeriod(selectedDate);
        if (!period) {
            setModalError('No hay un periodo anterior para asociar las emociones.');
            return;
        }
        previousEmotionsRef.current = [...existingEmotions];
        setEmotionsSaveStatus('idle');
        setIsSavingEmotions(true);
        setModalError(null);
        const fecha = selectedDate.toISOString().split('T')[0];
        try {
            const toCreate = selectedEmotions.filter(id => !existingEmotions.includes(id));
            const toDelete = existingEmotions.filter(id => !selectedEmotions.includes(id));
            if (toCreate.length > 0) {
                const createPayload = toCreate.map(id => ({
                    id_periodo: period.id_periodo,
                    id_emocion: id,
                    fecha,
                }));
                await emotionService.createEmotion(createPayload);
            }
            if (toDelete.length > 0) {
                const deletePayload = toDelete.map(id => ({
                    id_periodo: period.id_periodo,
                    id_emocion: id,
                    fecha,
                }));
                await emotionService.deleteEmotion(deletePayload);
            }
            const nextEmotions = [...selectedEmotions];
            setExistingEmotions(nextEmotions);
            await fetchAllEmotions();
            setEmotionsSaveStatus('success');
        } catch (e: any) {
            setEmotionsSaveStatus('error');
            const previous = previousEmotionsRef.current ? [...previousEmotionsRef.current] : [];
            setExistingEmotions(previous);
            setSelectedEmotions(previous);
            setModalError(e.message || 'Error al guardar las emociones.');
        } finally {
            setIsSavingEmotions(false);
        }
    };

    useEffect(() => {
        if (flowSaveStatus === 'idle') return;
        const timer = setTimeout(() => setFlowSaveStatus('idle'), 2000);
        return () => clearTimeout(timer);
    }, [flowSaveStatus]);

    useEffect(() => {
        if (symptomsSaveStatus === 'idle') return;
        const timer = setTimeout(() => setSymptomsSaveStatus('idle'), 2000);
        return () => clearTimeout(timer);
    }, [symptomsSaveStatus]);

    useEffect(() => {
        if (emotionsSaveStatus === 'idle') return;
        const timer = setTimeout(() => setEmotionsSaveStatus('idle'), 2000);
        return () => clearTimeout(timer);
    }, [emotionsSaveStatus]);

    // Crear periodo (recarga solo el periodo y flujo del día)
    const handleCreatePeriod = async () => {
        if (!user?.id_usuario || !selectedDate) {
            setModalError('Usuario no autenticado.');
            return;
        }

        const selectedDay = new Date(selectedDate);
        selectedDay.setHours(0, 0, 0, 0);
        const startDateString = selectedDay.toISOString().split('T')[0];

        const sortedPeriods = [...periods].sort(
            (a, b) => new Date(b.fecha_inicio).getTime() - new Date(a.fecha_inicio).getTime()
        );
        const previousPeriod = sortedPeriods.find(period => {
            const periodStart = new Date(period.fecha_inicio.split('T')[0] + 'T00:00:00');
            periodStart.setHours(0, 0, 0, 0);
            return periodStart < selectedDay;
        });

        let autoFillDays = periods.length === 0 ? 5 : 0;

        if (autoFillDays === 0 && previousPeriod?.fecha_termino) {
            const prevStart = new Date(previousPeriod.fecha_inicio.split('T')[0] + 'T00:00:00');
            const prevEnd = new Date(previousPeriod.fecha_termino.split('T')[0] + 'T00:00:00');
            prevStart.setHours(0, 0, 0, 0);
            prevEnd.setHours(0, 0, 0, 0);
            const diff = Math.round((prevEnd.getTime() - prevStart.getTime()) / DAY_IN_MS);
            if (diff >= 0) {
                autoFillDays = diff + 1;
            }
        }

        if (!autoFillDays && periodLength > 0) {
            autoFillDays = periodLength;
        }

        let computedEndDate: string | undefined;
        if (autoFillDays > 0) {
            const endDate = new Date(selectedDay);
            endDate.setDate(endDate.getDate() + autoFillDays - 1);
            computedEndDate = endDate.toISOString().split('T')[0];
        }

        setIsSavingPeriod(true);
        setModalError(null);

        try {
            const res = await periodService.createPeriod({
                id_usuario: user.id_usuario,
                fecha_inicio: startDateString,
                ...(computedEndDate ? { fecha_termino: computedEndDate } : {}),
            });

            setIsEditingPeriod(false);

            let createdPeriod: PeriodDto | null = null;
            const createdFromResponse = res && Array.isArray(res.periodos) && res.periodos.length > 0;

            if (createdFromResponse) {
                createdPeriod = res.periodos[0];
            } else {
                createdPeriod = {
                    id_periodo: Math.random(),
                    id_usuario: user.id_usuario,
                    fecha_inicio: startDateString,
                    ...(computedEndDate ? { fecha_termino: computedEndDate } : {}),
                } as PeriodDto;
            }

            if (createdPeriod) {
                setPeriods(prev => [createdPeriod, ...prev]);
                if (createdFromResponse) {
                    const flowRes = await flowService.getFlowByIdPeriodo(createdPeriod.id_periodo);
                    if (flowRes.statusCode === 200 && Array.isArray(flowRes.flujos)) {
                        const flowForDay = flowRes.flujos.find((f: any) => f.fecha === startDateString);
                        setExistingFlow(flowForDay || null);
                        if (flowForDay) {
                            const found = flujosCatalogo.find(f => f.id_flujo === flowForDay.id_flujo);
                            setFlowLevel(found || null);
                        } else {
                            setFlowLevel(null);
                        }
                    } else {
                        setExistingFlow(null);
                        setFlowLevel(null);
                    }
                } else {
                    setExistingFlow(null);
                    setFlowLevel(null);
                }
            }
            // No tocar openModal ni activar loading general
        } catch (e: any) {
            setModalError(e.message || 'Error al crear el periodo.');
        } finally {
            setIsSavingPeriod(false);
        }
    };

    // Extender periodo
    const handleExtendPeriod = async ({ period, direction }: { period: PeriodDto; direction: 'forward' | 'backward' }) => {
        if (!selectedDate) {
            setModalError('Fecha no seleccionada.');
            return;
        }
        setIsSavingPeriod(true);
        setModalError(null);
        try {
            const targetDate = selectedDate.toISOString().split('T')[0];
            const payload: UpdatePeriodRequestDto = {
                id_periodo: period.id_periodo,
                ...(direction === 'backward' ? { fecha_inicio: targetDate } : { fecha_termino: targetDate }),
            };
            const res = await periodService.updatePeriod(payload);
            setIsEditingPeriod(false);

            let nextPeriods: PeriodDto[];
            if (res && Array.isArray(res.periodos) && res.periodos.length > 0) {
                const actualizado = res.periodos[0];
                nextPeriods = periods.map(p => (p.id_periodo === actualizado.id_periodo ? actualizado : p));
            } else {
                nextPeriods = periods.map(p =>
                    p.id_periodo === payload.id_periodo
                        ? { ...p, ...payload }
                        : p
                );
            }
            setPeriods(nextPeriods);

            const updatedPeriod = nextPeriods.find(p => p.id_periodo === payload.id_periodo) || null;
            if (updatedPeriod) {
                const updatedStart = new Date(updatedPeriod.fecha_inicio.split('T')[0] + 'T00:00:00');
                updatedStart.setHours(0, 0, 0, 0);
                const updatedEnd = updatedPeriod.fecha_termino
                    ? new Date(updatedPeriod.fecha_termino.split('T')[0] + 'T00:00:00')
                    : new Date(updatedStart);
                updatedEnd.setHours(0, 0, 0, 0);

                const selectedNormalized = new Date(selectedDate);
                selectedNormalized.setHours(0, 0, 0, 0);

                if (selectedNormalized >= updatedStart && selectedNormalized <= updatedEnd) {
                    const flowRes = await flowService.getFlowByIdPeriodo(updatedPeriod.id_periodo);
                    if (flowRes.statusCode === 200 && Array.isArray(flowRes.flujos)) {
                        const flowForDay = flowRes.flujos.find((f: any) => f.fecha === targetDate);
                        setExistingFlow(flowForDay || null);
                        if (flowForDay) {
                            const found = flujosCatalogo.find(f => f.id_flujo === flowForDay.id_flujo);
                            setFlowLevel(found || null);
                        } else {
                            setFlowLevel(null);
                        }
                    } else {
                        setExistingFlow(null);
                        setFlowLevel(null);
                    }
                } else {
                    setExistingFlow(null);
                    setFlowLevel(null);
                }
            } else {
                setExistingFlow(null);
                setFlowLevel(null);
            }
        } catch (e: any) {
            setModalError(e.message || 'Error al extender el periodo.');
        } finally {
            setIsSavingPeriod(false);
        }
    };

    // Editar inicio/fin de periodo (preparar confirmación)
    const handleEditPeriod = ({ setStart = false, removeDay = false }: { setStart?: boolean; removeDay?: boolean }) => {
        if (!selectedDate) return;
        const period = findPeriodForDate(selectedDate);
        if (!period) return;

        const normalizedSelected = new Date(selectedDate);
        normalizedSelected.setHours(0, 0, 0, 0);

        const periodStart = new Date(period.fecha_inicio.split('T')[0] + 'T00:00:00');
        periodStart.setHours(0, 0, 0, 0);

        const currentEnd = period.fecha_termino
            ? new Date(period.fecha_termino.split('T')[0] + 'T00:00:00')
            : null;
        if (currentEnd) {
            currentEnd.setHours(0, 0, 0, 0);
        }

        if (setStart && removeDay) {
            return;
        }

        if (setStart) {
            const newStart = normalizedSelected.toISOString().split('T')[0];
            setPendingPeriodEdit({
                title: 'Actualizar inicio del periodo',
                description: `Se establecerá ${formatDateLabel(normalizedSelected)} como nueva fecha de inicio del periodo.`,
                confirmLabel: 'Guardar cambios',
                action: {
                    type: 'update',
                    payload: {
                        id_periodo: period.id_periodo,
                        fecha_inicio: newStart,
                    },
                },
            });
            return;
        }

        if (removeDay) {
            const newEndDate = new Date(normalizedSelected.getTime() - DAY_IN_MS);
            const newEndStr = newEndDate.toISOString().split('T')[0];

            if (newEndDate.getTime() < periodStart.getTime()) {
                setPendingPeriodEdit({
                    title: 'Eliminar periodo',
                    description: `Eliminar el día ${formatDateLabel(normalizedSelected)} quitará todo el periodo iniciado el ${formatDateLabel(periodStart)}.`,
                    confirmLabel: 'Eliminar periodo',
                    isDanger: true,
                    action: { type: 'delete', periodId: period.id_periodo },
                });
                return;
            }

            const hasFutureDays = currentEnd ? currentEnd.getTime() > normalizedSelected.getTime() : false;
            const message = hasFutureDays
                ? `Se eliminarán los días posteriores hasta ${formatDateLabel(currentEnd!)}. La nueva fecha de fin será ${formatDateLabel(newEndDate)}.`
                : `La nueva fecha de fin será ${formatDateLabel(newEndDate)}.`;

            setPendingPeriodEdit({
                title: 'Confirmar eliminación de día',
                description: message,
                confirmLabel: 'Actualizar periodo',
                action: {
                    type: 'update',
                    payload: {
                        id_periodo: period.id_periodo,
                        fecha_termino: newEndStr,
                    },
                },
            });
            return;
        }

        if (currentEnd && currentEnd.getTime() === normalizedSelected.getTime()) {
            return;
        }

        const hasFutureDays = currentEnd ? currentEnd.getTime() > normalizedSelected.getTime() : false;
        const message = hasFutureDays
            ? `Al marcar ${formatDateLabel(normalizedSelected)} como fin, se quitarán los días posteriores hasta ${formatDateLabel(currentEnd!)}.`
            : `Se marcará ${formatDateLabel(normalizedSelected)} como nueva fecha de fin del periodo.`;

        setPendingPeriodEdit({
            title: 'Confirmar fin del periodo',
            description: message,
            confirmLabel: 'Marcar fin',
            action: {
                type: 'update',
                payload: {
                    id_periodo: period.id_periodo,
                    fecha_termino: normalizedSelected.toISOString().split('T')[0],
                },
            },
        });
    };

    const handleConfirmPendingPeriodEdit = async () => {
        if (!pendingPeriodEdit || !selectedDate) {
            return;
        }

        setIsSavingPeriod(true);
        setModalError(null);

        try {
            const { action } = pendingPeriodEdit;

            if (action.type === 'delete') {
                await periodService.deletePeriod({ id_periodo: action.periodId });
                const nextPeriods = periods.filter(p => p.id_periodo !== action.periodId);
                setPeriods(nextPeriods);
                setExistingFlow(null);
                setFlowLevel(null);
                setShowDeleteWarning(false);
                setPendingPeriodEdit(null);
                setOpenModal(false);
                return;
            }

            const payload = action.payload;
            const res = await periodService.updatePeriod(payload);
            setIsEditingPeriod(false);

            let nextPeriods: PeriodDto[];
            if (res && Array.isArray(res.periodos) && res.periodos.length > 0) {
                const actualizado = res.periodos[0];
                nextPeriods = periods.map(p => (p.id_periodo === actualizado.id_periodo ? actualizado : p));
            } else {
                nextPeriods = periods.map(p =>
                    p.id_periodo === payload.id_periodo
                        ? { ...p, ...payload }
                        : p
                );
            }

            setPeriods(nextPeriods);

            const updatedPeriod = nextPeriods.find(p => p.id_periodo === payload.id_periodo) || null;
            if (updatedPeriod) {
                const updatedStart = new Date(updatedPeriod.fecha_inicio.split('T')[0] + 'T00:00:00');
                updatedStart.setHours(0, 0, 0, 0);
                const updatedEnd = updatedPeriod.fecha_termino
                    ? new Date(updatedPeriod.fecha_termino.split('T')[0] + 'T00:00:00')
                    : new Date(updatedStart);
                updatedEnd.setHours(0, 0, 0, 0);

                const selectedNormalized = new Date(selectedDate);
                selectedNormalized.setHours(0, 0, 0, 0);

                if (selectedNormalized >= updatedStart && selectedNormalized <= updatedEnd) {
                    const fecha = selectedDate.toISOString().split('T')[0];
                    const flowRes = await flowService.getFlowByIdPeriodo(updatedPeriod.id_periodo);
                    if (flowRes.statusCode === 200 && Array.isArray(flowRes.flujos)) {
                        const flowForDay = flowRes.flujos.find((f: any) => f.fecha === fecha);
                        setExistingFlow(flowForDay || null);
                        if (flowForDay) {
                            const found = flujosCatalogo.find(f => f.id_flujo === flowForDay.id_flujo);
                            setFlowLevel(found || null);
                        } else {
                            setFlowLevel(null);
                        }
                    } else {
                        setExistingFlow(null);
                        setFlowLevel(null);
                    }
                } else {
                    setExistingFlow(null);
                    setFlowLevel(null);
                }
            } else {
                setExistingFlow(null);
                setFlowLevel(null);
            }

            setPendingPeriodEdit(null);
        } catch (e: any) {
            setModalError(e.message || 'Error al editar el periodo.');
        } finally {
            setIsSavingPeriod(false);
        }
    };

    const handleCancelPendingPeriodEdit = () => {
        setPendingPeriodEdit(null);
    };

    // Eliminar periodo
    const handleDeletePeriod = async () => {
        if (!selectedDate) return;
        const period = findPeriodForDate(selectedDate);
        if (!period) return;
        setIsDeleting(true);
        setModalError(null);
        try {
            await periodService.deletePeriod({ id_periodo: period.id_periodo });
            setShowDeleteWarning(false);
            // Actualizar el estado local de periods
            setPeriods(prev => prev.filter(p => p.id_periodo !== period.id_periodo));
            setOpenModal(false);
        } catch (e: any) {
            setModalError(e.message || 'Error al eliminar el periodo.');
        } finally {
            setIsDeleting(false);
        }
    };

    // --- Lógica para abrir el modal y cargar datos ---
    const handleCloseModal = () => {
        setOpenModal(false);
        setShowDeleteWarning(false);
        setPendingPeriodEdit(null);
    };

    const isDateInFuture = (date: Date) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const normalized = new Date(date);
        normalized.setHours(0, 0, 0, 0);
        return normalized.getTime() > today.getTime();
    };

    const handleOpenModal = (date: Date) => {
        if (isDateInFuture(date)) {
            setFutureSelectionAlert({
                id: Date.now(),
                message: 'No puedes registrar datos en fechas futuras.',
            });
            return;
        }
        setPendingPeriodEdit(null);
        setShowDeleteWarning(false);
        setSelectedDate(date);
        setOpenModal(true);
        loadModalData(date);
    };

    // Obtener catálogos de flujo, síntomas y emociones (misma lógica que FirstTimeForm)
    const getCatalogos = async () => {
        try {
            const flujoRes = await flowService.getFlows();
            if (flujoRes.statusCode === 200) {
                setFlujosCatalogo(Array.isArray(flujoRes.flujo) ? flujoRes.flujo : []);
            } else {
                setFlujosCatalogo([]);
            }

            const sintomaRes = await symptomService.getSymptoms();
            if (sintomaRes.statusCode === 200) {
                setSintomasCatalogo(Array.isArray(sintomaRes.sintoma) ? sintomaRes.sintoma : []);
            } else {
                setSintomasCatalogo([]);
            }

            const emocionRes = await emotionService.getEmotions();
            if (emocionRes.statusCode === 200) {
                setEmocionesCatalogo(Array.isArray(emocionRes.emocion) ? emocionRes.emocion : []);
            } else {
                setEmocionesCatalogo([]);
            }
        } catch (err) {
            console.error('Error al obtener catálogos:', err);
            setFlujosCatalogo([]);
            setSintomasCatalogo([]);
            setEmocionesCatalogo([]);
        }
    };

    useEffect(() => {
        getCatalogos();
    }, []);

    // Estados para el cálculo del ciclo menstrual
    const [lastPeriodDate, setLastPeriodDate] = useState<string>('');
    const [periodLength, setPeriodLength] = useState<number>(1);
    const [nextPeriodDate, setNextPeriodDate] = useState<string>('-');
    const [daysUntilPeriod, setDaysUntilPeriod] = useState<string>('-');


    const getUserPeriods = useCallback(async () => {
        if (!user?.id_usuario) {
            setPeriods([]);
            setIsLoadingPeriods(false);
            return;
        }

        setIsLoadingPeriods(true);
        try {
            const response = await periodService.getPeriodsByUser({
                id_usuario: user.id_usuario,
            });

            if (response.statusCode === 200) {
                // Ordenar por fecha de inicio descendente (más reciente primero)
                const sorted = [...response.periodos].sort((a, b) => new Date(b.fecha_inicio).getTime() - new Date(a.fecha_inicio).getTime());
                setPeriods(sorted);
            } else {
                setPeriods([]);
            }
        } catch (err) {
            console.error('Error al obtener periodos:', err);
            setPeriods([]);
        } finally {
            setIsLoadingPeriods(false);
        }
    }, [user]);

    useEffect(() => {
        getUserPeriods();
    }, [getUserPeriods]);

    useEffect(() => {
        const now = new Date();
        now.setHours(0, 0, 0, 0);
        setCurrentDate(new Date(now));

        if (periods.length > 0) {
            const lastPeriod = periods[0];
            setLastPeriodDate(lastPeriod.fecha_inicio.split('T')[0]);

            if (lastPeriod.fecha_termino) {
                const startDate = new Date(lastPeriod.fecha_inicio.split('T')[0] + 'T00:00:00');
                const endDate = new Date(lastPeriod.fecha_termino.split('T')[0] + 'T00:00:00');
                const durationInDays = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;
                setPeriodLength(durationInDays);
            } else {
                const startDate = new Date(lastPeriod.fecha_inicio.split('T')[0] + 'T00:00:00');
                const daysElapsed = Math.ceil((now.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;
                setPeriodLength(daysElapsed <= 7 ? daysElapsed : 1);
            }
        } else {
            setLastPeriodDate('');
            setPeriodLength(0);
        }
    }, [periods]);

    // Función para calcular la información del próximo periodo usando la lógica real
    const calculateNextPeriodInfo = () => {
        if (!lastPeriodDate || periods.length === 0) return null;

        // Ordenar por fecha de inicio ascendente
        const sortedPeriods = [...periods].sort((a, b) => new Date(a.fecha_inicio).getTime() - new Date(b.fecha_inicio).getTime());
        let cycleLength = 28;
        let lastPeriodStart = new Date(lastPeriodDate + 'T00:00:00');
        if (sortedPeriods.length >= 2) {
            const lastIdx = sortedPeriods.length - 1;
            const prevStart = new Date(sortedPeriods[lastIdx - 1].fecha_inicio.split('T')[0] + 'T00:00:00');
            lastPeriodStart = new Date(sortedPeriods[lastIdx].fecha_inicio.split('T')[0] + 'T00:00:00');
            cycleLength = Math.round((lastPeriodStart.getTime() - prevStart.getTime()) / (1000 * 60 * 60 * 24));
        }
        // Si solo hay 1 periodo, usar ciclo estándar
        const nextPeriod = new Date(lastPeriodStart);
        nextPeriod.setDate(nextPeriod.getDate() + cycleLength);

        return {
            lastPeriod: lastPeriodStart,
            nextPeriod,
            cycleLength,
            periodLength,
        };
    };

    // Función para actualizar la información del ciclo
    const updateCycleInfo = () => {
        const cycle = calculateNextPeriodInfo();
        if (!cycle) {
            setNextPeriodDate('-');
            setDaysUntilPeriod('-');
            return;
        }

        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const daysUntil = Math.ceil((cycle.nextPeriod.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

        const nextPeriodFormatted = cycle.nextPeriod.toLocaleDateString('es-ES', {
            day: 'numeric',
            month: 'long',
        });

        setNextPeriodDate(capitalizeFirst(nextPeriodFormatted));
        if (daysUntil < 0) {
            setDaysUntilPeriod('-');
            return;
        }
        setDaysUntilPeriod(`${daysUntil} ${daysUntil === 1 ? 'día' : 'días'}`);
    };    // Actualizar información cuando cambian las fechas
    useEffect(() => {
        updateCycleInfo();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastPeriodDate, periodLength]);



    // // Cargar eventos al iniciar
    // useEffect(() => {
    //     const savedEvents = sessionStorage.getItem('calendarEvents');
    //     console.log('calendarEvents', sessionStorage.getItem('calendarEvents'));
    //     if (savedEvents) {
    //         try {
    //             // 1. Asegurarnos de que siempre sea un array válido
    //             const parsedEvents = JSON.parse(savedEvents);
    //             console.log('parserd events', JSON.parse(savedEvents));
    //             const safeEvents = Array.isArray(parsedEvents) ? parsedEvents : [];
    //             console.log('safe events', safeEvents);
    //             // 2. Normalizar fechas y filtrar eventos corruptos
    //             const normalizedEvents = safeEvents
    //                 .filter(event => event && typeof event === 'object')
    //                 .map(event => ({
    //                     id: Number(event.id),
    //                     title: String(event.title),
    //                     date: new Date(event.date).toISOString(),
    //                 }));

    //             setEvents(normalizedEvents);
    //         } catch (error) {
    //             console.error('Error al cargar eventos:', error);
    //             sessionStorage.removeItem('calendarEvents');
    //             setEvents([]); // Forzar array vacío si hay error
    //         }
    //     }
    // }, []);


    // // Guardar eventos cuando cambian
    // console.log('Renderizado - Events:', events.length);
    // useEffect(() => {
    //     sessionStorage.setItem('calendarEvents', JSON.stringify(events));
    //     console.log('Efecto de guardado ejecutado');
    // }, [events]);


    // Cambiar mes/año
    const changeDate = (offset: number, unit: 'month' | 'year') => {
        const newDate = new Date(currentDate);
        if (unit === 'month') newDate.setMonth(newDate.getMonth() + offset);
        if (unit === 'year') newDate.setFullYear(newDate.getFullYear() + offset);
        setCurrentDate(newDate);
    };

    // Obtener días del mes actual + días de meses adyacentes
    const getDaysInMonth = () => {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);

        // Ajuste para semana que empieza en lunes (0=lunes, 6=domingo)
        let startDay = firstDay.getDay() - 1;
        if (startDay < 0) startDay = 6;

        const days = [];

        // 1. Días del mes anterior
        if (startDay > 0) {
            const prevMonthLastDay = new Date(year, month, 0).getDate();
            for (let i = startDay; i > 0; i--) {
                const date = new Date(year, month - 1, prevMonthLastDay - i + 1);
                days.push({ day: date.getDate(), isCurrentMonth: false, date });
            }
        }

        // 2. Días del mes actual
        for (let i = 1; i <= lastDay.getDate(); i++) {
            const date = new Date(year, month, i);
            days.push({ day: i, isCurrentMonth: true, date });
        }

        // 3. Días del mes siguiente (solo si no termina en domingo)
        const endDay = lastDay.getDay() || 7;
        const daysNeeded = 7 - endDay;

        if (daysNeeded > 0) {
            for (let i = 1; i <= daysNeeded; i++) {
                const date = new Date(year, month + 1, i);
                days.push({ day: i, isCurrentMonth: false, date });
            }
        }

        return days;
    };

    const handleDaySelection = (dayDate: Date, isCurrentMonth: boolean) => {
        if (!isCurrentMonth) {
            const normalizedMonth = new Date(dayDate.getFullYear(), dayDate.getMonth(), 1);
            normalizedMonth.setHours(0, 0, 0, 0);
            setCurrentDate(normalizedMonth);
        }
        handleOpenModal(dayDate);
    };

    // Agregar evento
    // const addEvent = (date: Date, title: string) => {
    //     const newEvent: CalendarEvent = {
    //         id: Date.now(),
    //         title,
    //         date: date.toISOString(),
    //     };
    //     setEvents([...events, newEvent]);
    // };

    // // Eliminar evento
    // const deleteEvent = (id: number) => {
    //     setEvents(events.filter((event) => event.id !== id));
    // };

    // Función para determinar el tipo de día (periodos, ovulación y días fértiles)
    const getDayType = (date: Date) => {
        const checkDate = new Date(date);
        checkDate.setHours(0, 0, 0, 0);

        if (periods.length === 0) {
            return null;
        }

        // 1. Verificar si es un día de periodo registrado
        for (const period of periods) {
            const periodStartStr = period.fecha_inicio.split('T')[0];
            const periodStart = new Date(periodStartStr + 'T00:00:00');
            periodStart.setHours(0, 0, 0, 0);

            let periodEnd: Date;
            if (period.fecha_termino) {
                const periodEndStr = period.fecha_termino.split('T')[0];
                periodEnd = new Date(periodEndStr + 'T00:00:00');
            } else {
                periodEnd = new Date(periodStart);
            }
            periodEnd.setHours(0, 0, 0, 0);

            if (checkDate >= periodStart && checkDate <= periodEnd) {
                return 'period';
            }
        }

        // 2. Calcular ovulación y días fértiles SIEMPRE con ciclo real si hay 2 o más periodos
        const sortedPeriods = [...periods].sort((a, b) =>
            new Date(a.fecha_inicio).getTime() - new Date(b.fecha_inicio).getTime()
        );
        if (sortedPeriods.length >= 2) {
            return calculateFertilityFromRegisteredPeriods(checkDate, sortedPeriods);
        }

        // Si solo hay 1 periodo, usar ciclo estándar de 28 días
        if (periods.length === 1) {
            const lastPeriod = periods[0];
            return calculateFertilityFromStandardCycle(checkDate, lastPeriod);
        }

        return null;
    };

    // Función para calcular fertilidad basada en periodos registrados (múltiples ciclos)
    const calculateFertilityFromRegisteredPeriods = (checkDate: Date, sortedPeriods: PeriodDto[]) => {
        if (sortedPeriods.length >= 2) {
            const lastIdx = sortedPeriods.length - 1;
            const lastPeriod = sortedPeriods[lastIdx];
            const prevPeriod = sortedPeriods[lastIdx - 1];
            const lastStart = new Date(lastPeriod.fecha_inicio.split('T')[0] + 'T00:00:00');
            const prevStart = new Date(prevPeriod.fecha_inicio.split('T')[0] + 'T00:00:00');
            const cycleLength = Math.round((lastStart.getTime() - prevStart.getTime()) / (1000 * 60 * 60 * 24));
            // Estimar próximo inicio
            const estimatedNextStart = new Date(lastStart);
            estimatedNextStart.setDate(lastStart.getDate() + cycleLength);
            // Calcular ovulación y fértil estimados
            const ovulationDate = new Date(lastStart);
            ovulationDate.setDate(lastStart.getDate() + Math.floor(cycleLength / 2));
            const fertileStart = new Date(ovulationDate);
            fertileStart.setDate(fertileStart.getDate() - 5);
            const fertileEnd = new Date(ovulationDate);
            fertileEnd.setDate(fertileEnd.getDate() + 1);

            // Marcar fértil/ovulación entre el último inicio y el estimado del próximo
            if (checkDate > lastStart && checkDate < estimatedNextStart) {
                if (checkDate.getTime() === ovulationDate.getTime()) {
                    return 'ovulation';
                }
                if (checkDate >= fertileStart && checkDate <= fertileEnd) {
                    return 'fertile';
                }
            }

            // También marcar fértil/ovulación entre el penúltimo y el último inicio
            if (checkDate > prevStart && checkDate < lastStart) {
                const ovulationDatePrev = new Date(prevStart);
                ovulationDatePrev.setDate(prevStart.getDate() + Math.floor(cycleLength / 2));
                const fertileStartPrev = new Date(ovulationDatePrev);
                fertileStartPrev.setDate(fertileStartPrev.getDate() - 5);
                const fertileEndPrev = new Date(ovulationDatePrev);
                fertileEndPrev.setDate(fertileEndPrev.getDate() + 1);
                if (checkDate.getTime() === ovulationDatePrev.getTime()) {
                    return 'ovulation';
                }
                if (checkDate >= fertileStartPrev && checkDate <= fertileEndPrev) {
                    return 'fertile';
                }
            }
        }
        return null;
    };

    // Función para calcular fertilidad basada en ciclo estándar de 28 días
    const calculateFertilityFromStandardCycle = (checkDate: Date, lastPeriod: PeriodDto) => {
        const lastPeriodStart = new Date(lastPeriod.fecha_inicio.split('T')[0] + 'T00:00:00');

        // Calcular próximo periodo estimado (28 días después)
        const nextPeriodEstimated = new Date(lastPeriodStart);
        nextPeriodEstimated.setDate(nextPeriodEstimated.getDate() + 28);

        // Ovulación: 14 días antes del próximo periodo estimado
        const ovulationDate = new Date(nextPeriodEstimated);
        ovulationDate.setDate(ovulationDate.getDate() - 14);

        // Ventana fértil: 5 días antes de ovulación + día de ovulación + 1 día después
        const fertileStart = new Date(ovulationDate);
        fertileStart.setDate(fertileStart.getDate() - 5);
        const fertileEnd = new Date(ovulationDate);
        fertileEnd.setDate(fertileEnd.getDate() + 1);

        // Solo mostrar si la fecha está después del último periodo registrado
        // pero antes del próximo periodo estimado (NO marcar el próximo periodo)
        if (checkDate > lastPeriodStart && checkDate < nextPeriodEstimated) {
            if (checkDate.getTime() === ovulationDate.getTime()) {
                return 'ovulation';
            }
            if (checkDate >= fertileStart && checkDate <= fertileEnd) {
                return 'fertile';
            }
        }

        return null;
    };
    const days = getDaysInMonth();

    // --- Lógica para cargar datos al iniciar ---
    useEffect(() => {
        const today = new Date();
        setCurrentDate(new Date(today.getFullYear(), today.getMonth(), 1));
    }, []);

    // Renderizado
    if (isLoadingPeriods) {
        return <Loader text='Cargando periodos...' />;
    }

    // --- Alerta contextual para el día actual ---
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let todayAlert = null;
    // ¿Es día de sangrado?
    const periodToday = findPeriodForDate(today);
    // ¿Es fértil u ovulación?
    let fertilityType = null;
    if (!periodToday && periods.length > 0) {
        const sortedPeriods = [...periods].sort((a, b) => new Date(a.fecha_inicio).getTime() - new Date(b.fecha_inicio).getTime());
        fertilityType = calculateFertilityFromRegisteredPeriods(today, sortedPeriods);
        if (!fertilityType && sortedPeriods.length > 0) {
            fertilityType = calculateFertilityFromStandardCycle(today, sortedPeriods[sortedPeriods.length - 1]);
        }
    }
    if (periodToday && showAlerts.info) {
        todayAlert = (
            <FloatingAlert
                title='Hoy estás menstruando'
                message='Recuerda registrar tus síntomas y flujo.'
                mode='info'
                onClose={() => setShowAlerts(a => ({ ...a, info: false }))}
            />
        );
    } else if ((fertilityType === 'ovulation' || fertilityType === 'fertile') && showAlerts.danger) {
        todayAlert = (
            <FloatingAlert
                title={fertilityType === 'ovulation' ? '¡Hoy es tu ovulación!' : 'Día fértil'}
                message={fertilityType === 'ovulation' ? 'Este es el día más fértil de tu ciclo.' : 'Estás en tu ventana fértil.'}
                mode='danger'
                onClose={() => setShowAlerts(a => ({ ...a, danger: false }))}
            />
        );
    }

    return (
        <div className='calendar-page'>
            <div className='floating-alert-container'>
                {todayAlert}
                {futureSelectionAlert && (
                    <FloatingAlert
                        key={futureSelectionAlert.id}
                        title='Fecha futura'
                        message={futureSelectionAlert.message}
                        mode='warning'
                        duration={3500}
                        onClose={() => setFutureSelectionAlert(null)}
                    />
                )}
            </div>
            <div className='calendar-container'>
                {/* Banner del Calendario */}
                <div className='calendar-banner'>
                    <h1 className='calendar-title'>🌸 Mi Calendario Menstrual</h1>
                </div>

                {/* Sección de Información del Ciclo Menstrual */}
                <div className='calendar-info-section'>
                    {/* Primera fila: Campos de entrada */}
                    <div className='calendar-info-grid'>
                        <div className='calendar-info-card'>
                            <label htmlFor='lastPeriod'>Inicio último periodo:</label>
                            <div
                                style={{
                                    backgroundColor: 'white',
                                    border: '2px solid #e9d5ff',
                                    borderRadius: '6px',
                                    padding: '8px 12px',
                                    fontSize: '16px',
                                    color: '#333',
                                    minHeight: '20px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    textAlign: 'center',
                                }}
                            >
                                {lastPeriodDate ? capitalizeFirst(new Date(lastPeriodDate + 'T00:00:00').toLocaleDateString('es-ES', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: 'numeric',
                                }).replace(/\//g, '-')) : '-'}
                            </div>
                        </div>
                        <div className='calendar-info-card'>
                            <label htmlFor='periodLength'>Duración del último periodo (días):</label>
                            <div
                                style={{
                                    backgroundColor: 'white',
                                    border: '2px solid #e9d5ff',
                                    borderRadius: '6px',
                                    padding: '8px 12px',
                                    fontSize: '16px',
                                    color: '#333',
                                    minHeight: '20px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    textAlign: 'center',
                                }}
                            >
                                {periodLength > 0 ? `${periodLength} ${periodLength === 1 ? 'día' : 'días'}` : '-'}
                            </div>
                        </div>
                    </div>

                    {/* Segunda fila: Información calculada */}
                    <div className='calendar-info-grid'>
                        <div className='calendar-info-display'>
                            <h3>Próximo periodo estimado</h3>
                            <p>{nextPeriodDate}</p>
                        </div>
                        <div className='calendar-info-display'>
                            <h3>Días hasta el próximo periodo</h3>
                            <p>{daysUntilPeriod}</p>
                        </div>
                    </div>
                </div>

                {/* Calendario */}
                <div className='calendar-header'>
                    <button
                        className='calendar-nav-button'
                        onClick={() => changeDate(-1, 'year')}
                        aria-label='Año anterior'
                    >
                        ‹‹
                    </button>
                    <button
                        className='calendar-nav-button'
                        onClick={() => changeDate(-1, 'month')}
                        aria-label='Mes anterior'
                    >
                        ‹
                    </button>
                    <h2 className='calendar-month-title'>
                        {capitalizeFirst(currentDate.toLocaleString('es-ES', {
                            month: 'long',
                            year: 'numeric',
                        }))}
                    </h2>
                    <button
                        className='calendar-nav-button'
                        onClick={() => changeDate(1, 'month')}
                        aria-label='Mes siguiente'
                    >
                        ›
                    </button>
                    <button
                        className='calendar-nav-button'
                        onClick={() => changeDate(1, 'year')}
                        aria-label='Año siguiente'
                    >
                        ››
                    </button>
                    <div className='calendar-nav-buttons-mobile'>
                        <button
                            className='calendar-nav-button'
                            onClick={() => changeDate(-1, 'year')}
                            aria-label='Año anterior'
                        >
                            ‹‹
                        </button>
                        <button
                            className='calendar-nav-button'
                            onClick={() => changeDate(-1, 'month')}
                            aria-label='Mes anterior'
                        >
                            ‹
                        </button>
                        <button
                            className='calendar-nav-button'
                            onClick={() => changeDate(1, 'month')}
                            aria-label='Mes siguiente'
                        >
                            ›
                        </button>
                        <button
                            className='calendar-nav-button'
                            onClick={() => changeDate(1, 'year')}
                            aria-label='Año siguiente'
                        >
                            ››
                        </button>
                    </div>
                </div>

                <div className='calendar-days-grid'>
                    {['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'].map((day) => (
                        <div key={day} className='calendar-day-header'>
                            {day}
                        </div>
                    ))}
                    {days.map(({ day, isCurrentMonth, date }, index) => {
                        const isToday = new Date().toDateString() === date.toDateString();
                        const dayType = getDayType(date);
                        const dayStr = date.toISOString().split('T')[0];
                        const hasFlow = flowByDate[dayStr];
                        const hasSymptoms = symptomsByDate[dayStr];
                        const hasEmotions = emotionsByDate[dayStr];
                        const hasAnyData = hasFlow || hasSymptoms || hasEmotions;
                        const cellClasses = ['calendar-day'];
                        if (!isCurrentMonth) cellClasses.push('other-month');
                        if (isToday) cellClasses.push('today');
                        if (dayType) cellClasses.push(dayType);
                        if (hasAnyData) cellClasses.push('has-data');
                        if (!isCurrentMonth && hasAnyData) cellClasses.push('other-month-has-data');
                        return (
                            <div
                                key={index}
                                className={cellClasses.join(' ').trim()}
                                onClick={() => handleDaySelection(date, isCurrentMonth)}
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        handleDaySelection(date, isCurrentMonth);
                                    }
                                }}
                                style={{ position: 'relative' }}
                            >
                                <span
                                    className='calendar-day-number'
                                    style={{
                                        position: 'absolute',
                                        top: 2,
                                        left: 4,
                                        fontSize: '0.95em',
                                        fontWeight: 500,
                                        zIndex: 2,
                                    }}
                                >
                                    {day}
                                </span>
                                <span className='calendar-day-icons'>
                                    {hasAnyData && <span><PinIcon /></span>}
                                </span>
                            </div>
                        );
                    })}
                </div>

                {/* Leyenda */}
                <div className='calendar-legend'>
                    <div className='calendar-legend-item'>
                        <div className='calendar-legend-color legend-today'></div>
                        <span className='calendar-legend-text'>Hoy</span>
                    </div>
                    <div className='calendar-legend-item'>
                        <div className='calendar-legend-color legend-period'></div>
                        <span className='calendar-legend-text'>Periodo registrado</span>
                    </div>
                    <div className='calendar-legend-item'>
                        <div className='calendar-legend-color legend-fertile'></div>
                        <span className='calendar-legend-text'>Días fértiles</span>
                    </div>
                    <div className='calendar-legend-item'>
                        <div className='calendar-legend-color legend-ovulation'></div>
                        <span className='calendar-legend-text'>Ovulación</span>
                    </div>
                </div>
            </div>
            <ModalBase
                isOpen={openModal}
                onRequestClose={handleCloseModal}
                title={selectedDate ? `Datos para el día ${selectedDate.toLocaleDateString()}` : 'Datos para el día'}
                type='custom'
                footerButtons={[
                    <button key='close' className='btn btn-primary' onClick={handleCloseModal}>Cerrar</button>,
                ]}
            >
                <CalendarDayModal
                    isLoading={isLoadingModal}
                    error={modalError}
                    selectedDate={selectedDate || new Date()}
                    periodForDay={selectedDate ? findPeriodForDate(selectedDate) : null}
                    isFirstDay={(() => {
                        if (!selectedDate) return false;
                        const period = findPeriodForDate(selectedDate);
                        return !!(period && period.fecha_inicio.split('T')[0] === selectedDate.toISOString().split('T')[0]);
                    })()}
                    periodAction={(() => {
                        if (!selectedDate) return { action: 'none' as const };
                        const period = findPeriodForDate(selectedDate);
                        if (period) return { action: 'none' as const };

                        if (!periods.length) {
                            return { action: 'create' as const };
                        }

                        const normalizedSelected = new Date(selectedDate);
                        normalizedSelected.setHours(0, 0, 0, 0);

                        let closestForward: { period: PeriodDto; diff: number } | null = null;
                        let closestBackward: { period: PeriodDto; diff: number } | null = null;

                        for (const candidate of periods) {
                            const start = new Date(candidate.fecha_inicio.split('T')[0] + 'T00:00:00');
                            start.setHours(0, 0, 0, 0);
                            const end = candidate.fecha_termino
                                ? new Date(candidate.fecha_termino.split('T')[0] + 'T00:00:00')
                                : new Date(start);
                            end.setHours(0, 0, 0, 0);

                            if (normalizedSelected > end) {
                                const diff = Math.round((normalizedSelected.getTime() - end.getTime()) / DAY_IN_MS);
                                if (diff >= 1 && (!closestForward || diff < closestForward.diff)) {
                                    closestForward = { period: candidate, diff };
                                }
                            } else if (normalizedSelected < start) {
                                const diff = Math.round((start.getTime() - normalizedSelected.getTime()) / DAY_IN_MS);
                                if (diff >= 1 && (!closestBackward || diff < closestBackward.diff)) {
                                    closestBackward = { period: candidate, diff };
                                }
                            }
                        }

                        if (closestBackward && closestBackward.diff === 1) {
                            return {
                                action: 'extend' as const,
                                period: closestBackward.period,
                                direction: 'backward' as const,
                                diffDays: closestBackward.diff,
                            };
                        }

                        if (closestForward && closestForward.diff === 1) {
                            return {
                                action: 'extend' as const,
                                period: closestForward.period,
                                direction: 'forward' as const,
                                diffDays: closestForward.diff,
                            };
                        }

                        if (closestForward && closestForward.diff <= 5) {
                            return {
                                action: 'ask' as const,
                                period: closestForward.period,
                                direction: 'forward' as const,
                                diffDays: closestForward.diff,
                            };
                        }

                        if (closestBackward && closestBackward.diff <= 5) {
                            return {
                                action: 'ask' as const,
                                period: closestBackward.period,
                                direction: 'backward' as const,
                                diffDays: closestBackward.diff,
                            };
                        }

                        if (closestForward) {
                            return {
                                action: 'associate' as const,
                                period: closestForward.period,
                                direction: 'forward' as const,
                                diffDays: closestForward.diff,
                            };
                        }

                        return { action: 'create' as const };
                    })()}
                    showDeleteWarning={showDeleteWarning}
                    isEditingPeriod={isEditingPeriod}
                    isSavingPeriod={isSavingPeriod}
                    isDeleting={isDeleting}
                    isSavingFlow={isSavingFlow}
                    isDeletingFlow={isDeletingFlow}
                    isSavingSymptoms={isSavingSymptoms}
                    isSavingEmotions={isSavingEmotions}
                    flowSaveStatus={flowSaveStatus}
                    symptomsSaveStatus={symptomsSaveStatus}
                    emotionsSaveStatus={emotionsSaveStatus}
                    flowLevel={flowLevel}
                    existingFlow={existingFlow}
                    selectedSymptoms={selectedSymptoms}
                    existingSymptoms={existingSymptoms}
                    selectedEmotions={selectedEmotions}
                    existingEmotions={existingEmotions}
                    flujosCatalogo={flujosCatalogo}
                    sintomasCatalogo={sintomasCatalogo}
                    emocionesCatalogo={emocionesCatalogo}
                    onChangeFlowLevel={setFlowLevel}
                    onSaveFlow={handleSaveFlow}
                    onDeleteFlow={handleDeleteFlow}
                    onChangeSymptoms={setSelectedSymptoms}
                    onSaveSymptoms={handleSaveSymptoms}
                    onChangeEmotions={setSelectedEmotions}
                    onSaveEmotions={handleSaveEmotions}
                    onCreatePeriod={handleCreatePeriod}
                    onExtendPeriod={handleExtendPeriod}
                    onEditPeriod={handleEditPeriod}
                    onDeletePeriod={handleDeletePeriod}
                    onShowDeleteWarning={setShowDeleteWarning}
                    pendingPeriodEdit={pendingPeriodEdit}
                    onConfirmPendingPeriodEdit={handleConfirmPendingPeriodEdit}
                    onCancelPendingPeriodEdit={handleCancelPendingPeriodEdit}
                />
            </ModalBase>
        </div>
    );
};

export default CustomCalendar;